import { Dayjs } from "dayjs";

import { BusyDayDescription } from "stentor-models";

/**
 * Determines whether a calendar day counts as a bookable "business day" for the business.
 *
 * When `availableDays` is provided, those are the business's operating days — a business open
 * 7 days a week includes weekends. Only when `availableDays` is absent do we fall back to the
 * legacy Monday–Friday definition of a business day.
 */
export function isBusinessDay(day: Dayjs, busyDays: BusyDayDescription): boolean {
    if (Array.isArray(busyDays.availableDays) && busyDays.availableDays.length > 0) {
        const availableDays = busyDays.availableDays.map((d) => d.toLowerCase());
        return availableDays.includes(day.format("dddd").toLowerCase());
    }

    // No explicit availableDays — business days are Monday to Friday.
    return day.day() !== 0 && day.day() !== 6;
}

/**
 * Pure implementation of the default busy-day rules for the date picker.
 *
 * Returns true when the given date should be disabled (not selectable).
 *
 * @param d        The date being evaluated.
 * @param busyDays The busy-day configuration.
 * @param now      The current moment, used for "current day" and time-of-day checks.
 */
export function isDisabledByBusyDays(d: Dayjs, busyDays: BusyDayDescription, now: Dayjs): boolean {
    if (busyDays.blockWeekends && (d.day() === 0 || d.day() === 6)) {
        return true;
    }

    if (busyDays.blockCurrentDay && d.isSame(now, "day")) {
        return true;
    }

    if (busyDays.blockNextBusinessDays) {
        let nextBusinessDay = now;
        let count = 0;
        // Bound the search so a misconfigured availableDays can never loop forever.
        let guard = 0;
        while (count < busyDays.blockNextBusinessDays && guard < 366) {
            nextBusinessDay = nextBusinessDay.add(1, "day");
            guard++;
            // Only count days the business is actually open, so a 7-day-a-week business
            // doesn't skip weekends (the cause of Friday showing Monday instead of Saturday).
            if (isBusinessDay(nextBusinessDay, busyDays)) {
                count++;
            }
        }

        if (d.isBefore(nextBusinessDay, "day")) {
            return true;
        }
    }

    if (busyDays.currentDayAvailableUntil && d.isSame(now, "day")) {
        // currentDayAvailableUntil is 24 hour format (HH:mm), in the client's local time.
        const time = now.format("HH:mm");
        // if we are after the time, we should disable the current day
        if (time > busyDays.currentDayAvailableUntil) {
            return true;
        }
    }

    if (Array.isArray(busyDays.availableDays) && busyDays.availableDays.length > 0) {
        // availableDays are days of the week the business is open
        const availableDays = busyDays.availableDays.map((day) => day.toLowerCase());

        if (!availableDays.includes(d.format("dddd").toLowerCase())) {
            return true;
        }
    }

    return false;
}
